import React from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';

import withStyles from '@mui/styles/withStyles';
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import RadioGroup from '@mui/material/RadioGroup';

// core components
import Card from '../md/components/Card/Card.jsx';
import CardHeader from '../md/components/Card/CardHeader.jsx';
import CardText from '../md/components/Card/CardText.jsx';
import CardBody from '../md/components/Card/CardBody.jsx';

import { addPerson } from '../store/slices/personsSlice';
import { addVisibleFirmContact } from '../store/slices/orgContactsSlice';
import { updateFirm } from '../store/slices/firmsSlice';
import { setIntendedReferrerContactId } from '../store/slices/intendedInfoSlice';
import { setApiResponseInfo } from '../store/slices/apiResponseInfoSlice';

import {
  createStdRequest,
  apiFetch,
  validatePhoneNumber,
  validateEmail,
  sortedDropdownItemLabels,
  showAlert,
} from '../utils/utils';

import regularFormsStyle from '../assets/jss/crm/views/regularFormsStyle';

import DA from '../store/ReduxAccess';
import ReduxLoader from '../store/ReduxLoader.js';

import PhoneInput from './PhoneInput';
import CrmTextField from './CrmTextField';
import CrmButton from './CrmButton';
import CrmRadio from './CrmRadio';
import AlertModal from './AlertModal';
import KSelect from './KSelect';

const registerAccountFormStyles = (theme) => ({
  ...regularFormsStyle,

  contactInfoHeader: {
    display: 'inline-block',
    marginTop: '12px',
  },
  textFieldHolder: {
    marginTop: '16px',
  },
  contactPhoneInputDiv: {
    marginTop: '14px',
    marginBottom: '12px',
  },
  radioButtonDiv: {
    marginBottom: '10px',
  },
});

class NewFirmContactForm extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      redirectToFirmOverview: false,
      redirectToAccounts: false,
      redirectToLogin: false,
      shoudShowDuplicateUi: false,

      contactName: '',
      contactPhoneRoles: ['Work', 'Mobile'],
      contactPhoneNumber: '',
      contactEmail: '',
      contactJobTitle: '',
      contactDescription: '',
      ownerId: this.props.userInfo.personId,
      temperature: 'Unknown',

      duplicationDisposition: 'visitExisting',
    };
  }

  validationPolicy = {
    requireContactJobTitle: true,
    requireContactDescription: false,
    requireAtLeastOneOfContactPhoneOrEmail: true,
    requireContactPhone: false,
    requireContactEmail: false,
  };

  allPossiblePhoneRoles = ['Mobile', 'Business', 'Home', 'Work', 'Fax'];
  possibleContactPhoneRoles = ['Work', 'Mobile', 'Home'];

  possibleTemperatures = sortedDropdownItemLabels('Referral Contact Temperature');

  handleTextChange(event) {
    const rawValue = event.target.value;
    const value = event.target.id === 'contactEmail' ? rawValue.trim() : rawValue;
    this.setState({ [event.target.id]: value });
  }

  constructPhoneRolesArray(rolesObj) {
    let phoneRoles = [];
    this.allPossiblePhoneRoles.forEach((roleName) => {
      if (rolesObj[roleName]) {
        phoneRoles = phoneRoles.concat(roleName);
      }
    });
    return phoneRoles;
  }

  handleContactPhoneRolesChange(rolesObj) {
    const newPhoneRoles = this.constructPhoneRolesArray(rolesObj);
    this.setState({ contactPhoneRoles: newPhoneRoles });
  }

  handleContactPhoneNumberChange(newNumber) {
    this.setState({ contactPhoneNumber: newNumber });
  }

  validateForm() {
    let validation = this.validateContactName();
    if (!validation.valid) {
      return validation;
    }

    if (this.validationPolicy.requireContactJobTitle) {
      validation = this.validateContactJobTitle();
      if (!validation.valid) {
        return validation;
      }
    }

    if (this.validationPolicy.requireContactDescription) {
      validation = this.validateContactDescription();
      if (!validation.valid) {
        return validation;
      }
    }

    if (this.validationPolicy.requireContactPhone) {
      validation = this.validateContactPhone();
      if (!validation.valid) {
        return validation;
      }
    }

    if (this.validationPolicy.requireContactEmail) {
      validation = this.validateContactEmail();
      if (!validation.valid) {
        return validation;
      }
    }

    if (this.validationPolicy.requireAtLeastOneOfContactPhoneOrEmail) {
      validation = this.validateContactPhoneOrEmail();
      if (!validation.valid) {
        return validation;
      }
    }

    return { valid: true };
  }

  validateContactName() {
    const name = this.state.contactName.trim();
    if (name.length < 5) {
      return {
        valid: false,
        errorMessage: 'You need to provide a longer Name for the Contact',
      };
    }
    const firstSpaceLoc = name.indexOf(' ');
    if (firstSpaceLoc < 0) {
      return {
        valid: false,
        errorMessage: 'The Name for the Contact must have a space in the middle of it somewhere',
      };
    }
    return {
      valid: true,
    };
  }

  validateContactJobTitle() {
    if (this.state.contactJobTitle.trim().length === 0) {
      return { valid: false, errorMessage: 'You must supply a Job Title for the Contact' };
    } else {
      return { valid: true };
    }
  }

  validateContactDescription() {
    if (this.state.contactDescription.trim().length === 0) {
      return { valid: false, errorMessage: 'You must supply a Description for the Contact' };
    } else {
      return { valid: true };
    }
  }

  validateContactPhone() {
    return this.state.contactPhoneNumber
      ? this.validatePhoneNumberAndRoles(
          this.state.contactPhoneNumber,
          this.state.contactPhoneRoles,
          'Primary Contact Phone'
        )
      : { valid: false, errorMessage: 'You must supply a phone number for the Primary Contact' };
  }

  validateContactEmail() {
    return this.state.contactEmail
      ? validateEmail(this.state.contactEmail, 'Primary Contact Email does not appear to be in a valid format')
      : { valid: false, errorMessage: 'You must supply an email address for the Primary Contact' };
  }

  validatePhoneNumberAndRoles(number, roles, label) {
    if (number) {
      const validation = validatePhoneNumber(number, label);
      if (!validation.valid) {
        return validation;
      } else {
        return this.validateContactPhoneAndRolesConsistency(number, roles, label);
      }
    } else {
      return this.validateContactPhoneAndRolesConsistency(number, roles, label);
    }
  }

  validateContactPhoneAndRolesConsistency(number, roles, label) {
    if (number) {
      if (roles.length > 0) {
        return { valid: true };
      } else {
        return {
          valid: false,
          errorMessage: 'For ' + label + ', since you have supplied a number, you must also check a role',
        };
      }
    } else {
      if (roles.length > 0) {
        return {
          valid: false,
          errorMessage: 'For ' + label + ', since you have  checked a role, you must also supply a number',
        };
      } else {
        return { valid: true };
      }
    }
  }

  validateContactPhoneOrEmail() {
    if (this.state.contactEmail || this.state.contactPhoneNumber) {
      if (this.state.contactPhoneNumber) {
        const validation = this.validateContactPhone();
        if (!validation.valid) {
          return validation;
        }
      }
      if (this.state.contactEmail) {
        const validation = validateEmail(
          this.state.contactEmail,
          "The supplied Primary Contact Email doesn't appear to be valid"
        );
        if (!validation.valid) {
          return validation;
        }
      }
      return { valid: true };
    } else {
      return {
        valid: false,
        errorMessage: 'You must supply (at least) either a phone number or an email address for the Primary Contact',
      };
    }
  }

  populateFormWithSampleData() {
    this.setState({
      contactName: 'Marcus Welby',
      contactEmail: 'mark.welby.md@gmail.com',
      contactPhoneNumber: '213-234-5678',
      contactJobTitle: 'Client Advocate',
      contactDescription: 'Helpful to a fault',
      ownerId: this.props.userInfo.personId,
      temperature: 'Unknown',
    });
  }

  clearForm() {
    const freshState = {
      contactName: '',
      contactEmail: '',
      contactPhoneNumber: '',
      contactJobTitle: '',
      contactDescription: '',
      temperature: 'Unknown',
    };
    this.setState(freshState);
  }

  handleSubmit = () => {
    this.setState({ errorMessage: '' });

    const validation = this.validateForm();
    if (!validation.valid) {
      showAlert(this, validation.errorMessage);
      return;
    }
    if (this.looksLikeDuplicate()) {
      this.showDuplicateUi();
    } else {
      this.createFirmContact();
    }
  };

  looksLikeDuplicate() {
    const { visibleFirmId } = this.props;
    const existingContacts = DA.getContactsAtVisibleFirm(visibleFirmId);
    if (existingContacts.length === 0) {
      return false;
    }

    const nameStruct = this.parseContactName();
    const { firstName, lastName } = nameStruct;
    const existingContactsWithSameName = existingContacts.filter(
      (c) => c.firstName === firstName && c.lastName === lastName
    );
    if (existingContactsWithSameName.length === 0) {
      return false;
    }

    const email = this.state.contactEmail;
    if (email) {
      return existingContactsWithSameName.some((c) => c.email === email);
    }

    const phoneNumber = this.state.contactPhoneNumber;
    if (phoneNumber) {
      return existingContactsWithSameName.some((c) => this.hasPhoneNumber(c, phoneNumber));
    }

    return true; // same name, but no phone number or email
  }

  hasPhoneNumber(contact, phoneNumber) {
    let foundMatch = false;
    contact.phoneNumbers.forEach((pn) => {
      if (pn.number === phoneNumber) {
        foundMatch = true;
      }
    });
    return foundMatch;
  }

  showDuplicateUi() {
    this.setState({ shoudShowDuplicateUi: true });
  }

  handleDuplicateChoiceSubmit() {
    if (this.state.duplicationDisposition === 'visitExisting') {
      const contactId = this.getDuplicateContactId();
      this.props.setIntendedReferrerContactId(contactId);
      this.setState({ redirect: 'referrerContact' });
    } else {
      this.createFirmContact();
    }
  }

  getDuplicateContactId() {
    const { visibleFirmId } = this.props;
    const existingContacts = DA.getContactsAtVisibleFirm(visibleFirmId);
    if (existingContacts.length === 1) {
      return existingContacts[0].personId;
    }

    const nameStruct = this.parseContactName();
    const { firstName, lastName } = nameStruct;
    const existingContactsWithSameName = existingContacts.filter(
      (c) => c.firstName === firstName && c.lastName === lastName
    );
    if (existingContactsWithSameName.length === 1) {
      return existingContactsWithSameName[0].personId;
    }

    const email = this.state.contactEmail;
    if (email) {
      const existingContactsWithSameEmail = existingContactsWithSameName.filter((c) => c.email === email);
      if (existingContactsWithSameEmail.length === 1) {
        return existingContactsWithSameEmail[0].personId;
      }
    }

    const phoneNumber = this.state.contactPhoneNumber;
    if (phoneNumber) {
      const existingContactsWithSamePhone = existingContactsWithSameName.filter((c) =>
        this.hasPhoneNumber(c, phoneNumber)
      );
      if (existingContactsWithSamePhone.length === 1) {
        return existingContactsWithSamePhone[0].personId;
      }
    }

    return existingContactsWithSameName[0].personId; // same name, but no phone number or email
  }

  createNewContactApiStruct(viewingFirmId, visibleFirmId) {
    const nameStruct = this.parseContactName();
    const { firstName, middleNames, lastName } = nameStruct;
    const preferredName = firstName;
    const addresses = [];

    const contactApiStruct = {
      personId: 0,
      email: this.state.contactEmail,
      firstName,
      middleNames,
      lastName,
      addresses,
      preferredName,
      sex: 'Unknown',
      firmId: visibleFirmId,
      viewingFirmId,
      jobTitle: this.state.contactJobTitle.trim(),
      description: this.state.contactDescription.trim(),
      ownerId: this.state.ownerId,
      temperature: this.state.temperature,
    };
    if (this.state.contactPhoneNumber) {
      const phoneRec = {
        roles: this.state.contactPhoneRoles.join(','),
        countryCode: '1',
        number: this.state.contactPhoneNumber,
      };
      contactApiStruct.phoneNumbers = [phoneRec];
    } else {
      contactApiStruct.phoneNumbers = [];
    }
    return contactApiStruct;
  }

  createFirmContact() {
    const { viewingFirmId, visibleFirmId } = this.props;
    const contactApiStruct = this.createNewContactApiStruct(viewingFirmId, visibleFirmId);
    const uri = `/api/firm/${viewingFirmId}/visibleFirm/${visibleFirmId}/contact`;
    const request = createStdRequest(uri, 'POST', contactApiStruct);
    const actionOnGoodResponse = (json) => {
      const reduxifiedContact = ReduxLoader.createReduxStructForFetchedPersonOrContactApiStruct(json);
      this.addNewContactToReduxStore(reduxifiedContact);
      this.clearForm();
      this.props.successFunc(json.personId);
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  parseContactName() {
    const trimmedName = this.state.contactName.trim();
    const firstSpaceLoc = trimmedName.indexOf(' ');
    const lastSpaceLoc = trimmedName.lastIndexOf(' ');
    const firstName = trimmedName.substring(0, firstSpaceLoc);
    const lastName = trimmedName.substring(lastSpaceLoc + 1);
    const middleNames =
      lastSpaceLoc > firstSpaceLoc ? trimmedName.substring(firstSpaceLoc + 1, lastSpaceLoc).trim() : '';
    return { firstName, middleNames, lastName };
  }

  addNewContactToReduxStore(contactApiStruct) {
    const contactId = contactApiStruct.personId;
    const preferredFullName = contactApiStruct.preferredName + ' ' + (contactApiStruct.lastName || '');
    const augmentedPerson = { ...contactApiStruct, preferredFullName };
    this.props.addPerson(augmentedPerson);
    const visibleFirmId = this.props.visibleFirmId;
    this.props.addVisibleFirmContact({ visibleFirmId, contactId });
    this.updateFirmWithNewId(visibleFirmId, contactId);
  }

  updateFirmWithNewId(visibleFirmId, contactId) {
    const oldFirmState = DA.getFirmById(visibleFirmId);
    const oldContactIdList = oldFirmState.contactIds;
    const newContactIdList = oldContactIdList.concat(contactId);
    const newFirmState = { ...oldFirmState, contactIds: newContactIdList };
    this.props.updateFirm(newFirmState);
  }

  handleDuplicationDispositionChange = (event) => {
    this.setState({ duplicationDisposition: event.target.value });
  };

  setOwnerId(newValue) {
    this.setState({ ownerId: newValue });
  }

  setTemperature(newValue) {
    this.setState({ temperature: newValue });
  }

  render() {
    if (this.state.redirect) {
      return <Redirect to={this.state.redirect} push />;
    }

    if (this.state.redirectToLogin) {
      return <Redirect to="login" push />;
    }

    if (this.state.redirectToFirmOverview) {
      return <Redirect to="firmOverview" push />;
    }

    if (this.state.redirectToAccounts) {
      return <Redirect to="accounts" push />;
    }

    const { classes, visibleFirmId } = this.props;
    const onLocalHost = window.location.hostname === 'localhost';
    const possibleOwners = DA.getAllFirmUsers();
    const visibleFirm = DA.getFirmById(visibleFirmId);
    const emailIsRequired = !this.state.contactPhoneNumber;

    return (
      <div>
        <AlertModal show={this.state.shouldShowAlertModal} renderer={this} text={this.state.alertModalText} />

        <Grid container>
          <Grid item xs={12} sm={12} md={12}>
            <Card>
              <CardHeader color="allevaOrange" text>
                <CardText color="allevaOrange">
                  <h4 className={classes.cardTitle}>Create Contact</h4>
                </CardText>
              </CardHeader>
              {this.state.shoudShowDuplicateUi ? (
                <CardBody>
                  <Grid container>
                    <Grid item xs={12} sm={12}>
                      <h4>We already have a contact with these identifiers at {visibleFirm.name}.</h4>
                      <h4>Would you like to work on the profile of that existing contact?</h4>
                      <div className={classes.radioButtonDiv}>
                        <FormControl variant="standard" component="fieldset" className={classes.formControl}>
                          <RadioGroup
                            aria-label="Duplication disposition"
                            name="duplication-disposition"
                            value={this.state.duplicationDisposition}
                            onChange={this.handleDuplicationDispositionChange}
                          >
                            <FormControlLabel
                              value="visitExisting"
                              control={<CrmRadio />}
                              label="Yes, visit existing contact"
                            />
                            <FormControlLabel
                              value="createNew"
                              control={<CrmRadio />}
                              label="No, create a new contact"
                            />
                          </RadioGroup>
                        </FormControl>
                      </div>
                    </Grid>
                  </Grid>
                  <Grid container justifyContent="flex-end">
                    <Grid item xs={12} sm={12} md={12}>
                      <CrmButton filled onClick={() => this.handleDuplicateChoiceSubmit()}>
                        {this.state.duplicationDisposition === 'visitExisting' ? 'Go' : 'Create Contact'}
                      </CrmButton>
                    </Grid>
                  </Grid>
                </CardBody>
              ) : (
                <CardBody>
                  <Grid container>
                    <Grid item xs={12} sm={12}>
                      <h4 className={classes.contactInfoHeader}>Contact</h4>
                      <CrmTextField
                        label="Name"
                        id="contactName"
                        required
                        fullWidth
                        value={this.state.contactName}
                        onChange={(event) => this.handleTextChange(event)}
                      />
                      <div className={classes.textFieldHolder}>
                        <CrmTextField
                          label="Job Title"
                          id="contactJobTitle"
                          required
                          fullWidth
                          value={this.state.contactJobTitle}
                          onChange={(event) => this.handleTextChange(event)}
                        />
                      </div>
                      <div className={classes.textFieldHolder}>
                        <CrmTextField
                          label="Description"
                          id="contactDescription"
                          fullWidth
                          value={this.state.contactDescription}
                          onChange={(event) => this.handleTextChange(event)}
                        />
                      </div>
                      <div className={classes.textFieldHolder}>
                        <CrmTextField
                          label="Email Address"
                          id="contactEmail"
                          required={emailIsRequired}
                          fullWidth
                          value={this.state.contactEmail}
                          onChange={(event) => this.handleTextChange(event)}
                        />
                      </div>
                      <div className={classes.contactPhoneInputDiv}>
                        <PhoneInput
                          label="Contact Phone"
                          possibleRoles={this.possibleContactPhoneRoles}
                          actualRoles={this.state.contactPhoneRoles}
                          number={this.state.contactPhoneNumber}
                          onRolesChange={(rolesObj) => this.handleContactPhoneRolesChange(rolesObj)}
                          onNumberChange={(newNumber) => this.handleContactPhoneNumberChange(newNumber)}
                        />
                      </div>
                      <div className={classes.textFieldHolder}>
                        <KSelect
                          label="Owner"
                          fullWidth={true}
                          liveOptions={possibleOwners.map((c) => c.name)}
                          liveValues={possibleOwners.map((c) => c.personId)}
                          value={this.state.ownerId}
                          onChange={(event) => this.setOwnerId(event.target.value)}
                        />
                      </div>
                      <div className={classes.textFieldHolder}>
                        <KSelect
                          label="Temperature"
                          fullWidth={true}
                          liveOptions={this.possibleTemperatures}
                          liveValues={this.possibleTemperatures}
                          value={this.state.temperature}
                          onChange={(event) => this.setTemperature(event.target.value)}
                        />
                      </div>
                    </Grid>
                  </Grid>
                  <Grid container justifyContent="flex-end" style={{ marginTop: '14px' }}>
                    <Grid item xs={12} sm={12} md={12}>
                      <CrmButton filled onClick={() => this.handleSubmit()}>
                        Create Contact
                      </CrmButton>
                      {onLocalHost && (
                        <CrmButton color="secondary" onClick={() => this.populateFormWithSampleData()}>
                          Populate Form with Sample Data
                        </CrmButton>
                      )}
                    </Grid>
                  </Grid>
                </CardBody>
              )}
            </Card>
          </Grid>
        </Grid>
      </div>
    );
  }
}

const actionProducers = {
  setApiResponseInfo,
  addPerson,
  addVisibleFirmContact,
  updateFirm,
  setIntendedReferrerContactId,
};

const mapStateToProps = (state) => ({ userInfo: state.userInfo });

export default connect(mapStateToProps, actionProducers)(withStyles(registerAccountFormStyles)(NewFirmContactForm));
